import { useScienceTutorSection } from './scienceTutorContext';
import { Eye, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import {
  RUST_LEVEL_LABELS,
  RUST_LEVEL_ORDER,
  checkRustPrediction,
  type RustCase,
  type RustLevel,
} from './combustionCorrosion';
import { ConditionChips, FeedbackPanel, RustTube } from './CombustionCorrosionVisuals';

interface RustInvestigationProps {
  cases: RustCase[];
  /** Year 6 shows the conditions only after the reveal so the learner reasons from the setup. */
  showConditionsBeforeReveal: boolean;
}

export function RustInvestigation({ cases, showConditionsBeforeReveal }: RustInvestigationProps) {
  const [caseId, setCaseId] = useState(cases[0].id);
  const [prediction, setPrediction] = useState<RustLevel | null>(null);
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('RustInvestigation', { caseId, prediction, revealed });
  const entry = cases.find((candidate) => candidate.id === caseId) ?? cases[0];
  const result = revealed && prediction ? checkRustPrediction(entry, prediction) : null;

  const choose = (next: RustCase) => {
    setCaseId(next.id);
    setPrediction(null);
    setRevealed(false);
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Choose a tube</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-1" aria-label="Rusting test tubes">
          {cases.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              aria-pressed={candidate.id === entry.id}
              onClick={() => choose(candidate)}
              className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                candidate.id === entry.id
                  ? 'border-brand-sky bg-wash-sky text-ink'
                  : 'border-hairline bg-surface text-slate2 hover:border-brand-sky/40'
              }`}
            >
              {candidate.title}
            </button>
          ))}
        </div>
      </aside>

      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <div className="grid gap-6 md:grid-cols-[auto_minmax(0,1fr)]">
          <RustTube entry={entry} level={result ? result.expected : null} />
          <div className="min-w-0">
            <h3 className="text-xl font-extrabold text-ink">{entry.title}</h3>
            <p className="mt-2 font-medium leading-7 text-slate2">{entry.context}</p>
            {showConditionsBeforeReveal || revealed ? (
              <div className="mt-4">
                <ConditionChips entry={entry} />
              </div>
            ) : null}

            <fieldset className="mt-5">
              <legend className="text-sm font-extrabold text-ink">
                Predict: what will the nail look like after about one week?
              </legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                {RUST_LEVEL_ORDER.map((level) => (
                  <button
                    key={level}
                    type="button"
                    disabled={revealed}
                    aria-pressed={prediction === level}
                    onClick={() => setPrediction(level)}
                    className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold disabled:cursor-default ${
                      prediction === level ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'
                    }`}
                  >
                    {RUST_LEVEL_LABELS[level]}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                disabled={!prediction || revealed}
                onClick={() => setRevealed(true)}
                className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Eye className="h-4 w-4" aria-hidden="true" /> Leave for a week
              </button>
              <button
                type="button"
                onClick={() => choose(entry)}
                className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset
              </button>
            </div>
          </div>
        </div>

        {result ? (
          <FeedbackPanel
            correct={result.correct}
            title={
              result.correct
                ? `Correct — ${RUST_LEVEL_LABELS[result.expected]}`
                : `Not quite — the model shows ${RUST_LEVEL_LABELS[result.expected].toLowerCase()}`
            }
          >
            <p>{result.feedback}</p>
            <p>
              <strong className="text-ink">Model rule:</strong> {result.reason} Rust needs water and
              oxygen together.
            </p>
          </FeedbackPanel>
        ) : null}
      </section>
    </div>
  );
}
